/* ============================================================
   IntroPanel — dismissible module introduction
   Proposal 6 Tier 1 (2026-04-24)

   Renders a 2-3 sentence "what is this module?" primer at the
   top of each module's overview. Dismissible per module,
   persisted in localStorage, re-openable from Settings later
   (post-implementation).

   Safe against malformed localStorage per E-021 Tier 2 rules:
   every localStorage read is guarded; wrong-shape value returns
   an empty dismissed-map (component renders normally).
   ============================================================ */
import React, { useState, useCallback } from 'react';
import { MODULE_INTROS } from '../content/module-intros.js';

const DISMISSED_KEY = 'lens-intro-dismissed-v1';

function loadDismissedMap() {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function persistDismissedMap(map) {
  try { localStorage.setItem(DISMISSED_KEY, JSON.stringify(map)); } catch { /* swallow */ }
}

export default function IntroPanel({ moduleId }) {
  const intro = MODULE_INTROS[moduleId];
  const [dismissed, setDismissed] = useState(() => !!loadDismissedMap()[moduleId]);

  const dismiss = useCallback(() => {
    setDismissed(true);
    const map = loadDismissedMap();
    map[moduleId] = true;
    persistDismissedMap(map);
  }, [moduleId]);

  if (!intro || dismissed) return null;

  return (
    <div
      role="note"
      className="mx-4 mb-3 mt-2 px-4 py-3 bg-[rgba(240,180,41,0.06)] border border-[rgba(240,180,41,0.25)] rounded-sm flex items-start gap-3"
    >
      <span className="text-accent-gold text-[1.05rem] leading-none pt-[3px] shrink-0" aria-hidden>✦</span>
      <div className="flex-1 min-w-0">
        <strong className="text-accent-gold text-[0.7rem] font-semibold uppercase tracking-[0.08em] block mb-[0.35rem]">
          {intro.title}
        </strong>
        <p className="text-text-dim text-[0.82rem] leading-relaxed m-0">{intro.body}</p>
      </div>
      <button
        type="button"
        onClick={dismiss}
        className="text-text-muted hover:text-text focus-visible:text-text bg-transparent border-0 cursor-pointer text-[1.15rem] leading-none shrink-0 px-[6px] py-[2px] font-[inherit] rounded-sm hover:bg-[rgba(255,255,255,0.04)]"
        aria-label={`Dismiss introduction for ${intro.title}`}
        title="Dismiss (you can re-enable introductions in Settings · Guide controls)"
      >
        ×
      </button>
    </div>
  );
}
